import { NodeProps } from 'reactflow';
import { faBolt } from '@fortawesome/pro-solid-svg-icons';
import { CampaignBadges } from 'assets/js/pages/engage/workflows/components/CampaignBadges';
import {
    CampaignSelect,
    defaultCampaignSelectValue,
} from 'assets/js/pages/engage/workflows/components/CampaignSelect';
import * as CanvasNode from 'assets/js/pages/engage/workflows/components/CanvasNode';
import * as NodeBadges from 'assets/js/pages/engage/workflows/components/NodeBadges';
import { StepFormContainer } from 'assets/js/pages/engage/workflows/components/StepFormContainer';
import { useDebouncedAutosave } from 'assets/js/pages/engage/workflows/hooks/useDebouncedAutosave';
import { useSyncFormToNode } from 'assets/js/pages/engage/workflows/hooks/useSyncFormToNode';
import { useTriggerValidationOnDeselection } from 'assets/js/pages/engage/workflows/hooks/useTriggerValidationOnDeselection';
import {
    AbandonedDonationFormTriggerFormValues,
    AbandonedDonationFormTriggerNodeData,
} from 'assets/js/pages/engage/workflows/triggers/types';
import { enabledTriggers } from 'assets/js/pages/engage/workflows/utils/starterItems';
import * as Form from 'components/form';
import { WorkflowTriggerTypeEnum } from 'constants/workflows';

interface AbandonedDonationFormTriggerProps
    extends NodeProps<AbandonedDonationFormTriggerNodeData> {}

const AbandonedDonationFormTrigger = ({
    id,
    selected,
    data,
}: AbandonedDonationFormTriggerProps) => {
    const { formData } = data;
    const methods = Form.useGivebutterForm<AbandonedDonationFormTriggerFormValues>({
        mode: 'all',
        defaultValues: {
            campaigns: formData?.values.campaigns ?? defaultCampaignSelectValue,
        },
    });
    const {
        control,
        formState: { errors },
        getValues,
        trigger,
    } = methods;
    const isError = Object.keys(errors).length > 0;

    useSyncFormToNode<AbandonedDonationFormTriggerFormValues>({ nodeId: id, control });
    useDebouncedAutosave<AbandonedDonationFormTriggerFormValues>({ methods });
    useTriggerValidationOnDeselection<AbandonedDonationFormTriggerFormValues>({
        trigger,
        selected,
    });

    const selectedTrigger = enabledTriggers.find(
        t => t.slug === WorkflowTriggerTypeEnum.ABANDONED_DONATION_FORM,
    );
    if (!selectedTrigger) return;

    return (
        <Form.Provider {...methods}>
            <StepFormContainer>
                <CanvasNode.Root
                    className="max-w-96"
                    isSelected={selected}
                    isError={isError}
                    id={id}
                >
                    <CanvasNode.Container>
                        <CanvasNode.Header>
                            <CanvasNode.Icon icon={faBolt} className="text-yellow-300" />
                            <div className="flex flex-col items-start">
                                <CanvasNode.Title>{selectedTrigger.name}</CanvasNode.Title>
                                {selected ? (
                                    <CanvasNode.Description>
                                        Configure your workflow trigger
                                    </CanvasNode.Description>
                                ) : null}
                            </div>
                        </CanvasNode.Header>
                        <CanvasNode.Body>
                            <CanvasNode.BodySelected>
                                <CampaignSelect />
                            </CanvasNode.BodySelected>
                            <CanvasNode.BodyUnselected>
                                <AbandonedDonationFormValuesDisplay values={getValues()} />
                            </CanvasNode.BodyUnselected>
                        </CanvasNode.Body>
                    </CanvasNode.Container>
                </CanvasNode.Root>
            </StepFormContainer>
        </Form.Provider>
    );
};

interface AbandonedDonationFormValuesDisplayProps {
    values: AbandonedDonationFormTriggerFormValues;
    showLabel?: boolean;
}
const AbandonedDonationFormValuesDisplay = ({
    values: { campaigns },
    showLabel = false,
}: AbandonedDonationFormValuesDisplayProps) => {
    const trigger = enabledTriggers.find(
        t => t.slug === WorkflowTriggerTypeEnum.ABANDONED_DONATION_FORM,
    );
    if (!trigger) return null;

    return (
        <div className="flex flex-col gap-2">
            {showLabel ? <p className="text-sm font-medium text-gray-900">{trigger.name}</p> : null}
            <NodeBadges.Root>
                <CampaignBadges campaigns={campaigns} />
            </NodeBadges.Root>
        </div>
    );
};

export { AbandonedDonationFormTrigger, AbandonedDonationFormValuesDisplay };
